import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';

function EditMeetingPanel({ meeting, isOpen, onClose, onSave }) {
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  // HM's timezone from the meeting object (set by backend from HM's user profile)
  const hmTimezone = meeting?.hiring_manager_timezone || 'America/New_York';

  // Short label e.g. "New York" or "Chicago"
  const tzLabel = hmTimezone.split('/').pop().replace(/_/g, ' ');

  // HM's current local time string for display
  const currentHmTime = (() => {
    try {
      return new Date().toLocaleTimeString('en-US', {
        timeZone: hmTimezone,
        hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short'
      });
    } catch { return ''; }
  })();

  // Convert UTC date+time (stored in DB) → wall-clock time in HM's timezone
  // Used only for DISPLAY of the current schedule
  const utcToHmDisplay = (dateStr, timeStr) => {
    try {
      const utc = new Date(`${dateStr}T${timeStr}:00Z`);
      const date = utc.toLocaleDateString('en-US', {
        timeZone: hmTimezone, month: 'long', day: 'numeric', year: 'numeric'
      });
      const time = utc.toLocaleTimeString('en-US', {
        timeZone: hmTimezone, hour: '2-digit', minute: '2-digit', hour12: true
      });
      return { date, time };
    } catch { return { date: dateStr, time: timeStr }; }
  };

  // Convert UTC → HM local for pre-filling the input fields (as YYYY-MM-DD / HH:MM)
  const utcToHmInputValues = (dateStr, timeStr) => {
    try {
      const utc = new Date(`${dateStr}T${timeStr}:00Z`);
      // Use Intl to get date parts in the HM's timezone
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: hmTimezone,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(utc);

      const get = (type) => parts.find(p => p.type === type)?.value || '';
      const inputDate = `${get('year')}-${get('month')}-${get('day')}`;
      const h = get('hour') === '24' ? '00' : get('hour').padStart(2, '0');
      const inputTime = `${h}:${get('minute').padStart(2, '0')}`;
      return { inputDate, inputTime };
    } catch { return { inputDate: dateStr, inputTime: timeStr }; }
  };

  // Populate inputs when panel opens — show time in HM's timezone
  useEffect(() => {
    if (!isOpen) {
      setScheduledDate('');
      setScheduledTime('');
      setError('');
      return;
    }
    if (meeting?.scheduled_date && meeting?.scheduled_time) {
      const { inputDate, inputTime } = utcToHmInputValues(
        meeting.scheduled_date,
        meeting.scheduled_time
      );
      setScheduledDate(inputDate);
      setScheduledTime(inputTime);
    }
  }, [meeting?.id, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!scheduledDate || !scheduledTime) {
      setError('Please fill in both date and time');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');

      // Send time as-entered (in HM's timezone) + hm_timezone
      // Backend converts to UTC exactly like POST /schedule does — no frontend math needed
      const params = `scheduled_date=${scheduledDate}&scheduled_time=${scheduledTime}&hm_timezone=${encodeURIComponent(hmTimezone)}`;

      const response = await fetch(
        API_ENDPOINTS.MEETINGS_UPDATE(meeting.id, params),
        { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const result = await response.json();
        onSave(result.data);
        onClose();
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to update meeting');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const currentSchedule = meeting?.scheduled_date && meeting?.scheduled_time
    ? utcToHmDisplay(meeting.scheduled_date, meeting.scheduled_time)
    : null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />

      {/* Slide panel */}
      <div className="fixed right-0 top-0 h-full w-full md:w-[500px] bg-white shadow-2xl z-50">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Edit Meeting</h2>
            <p className="text-blue-100 text-sm mt-1">{meeting?.meeting_title}</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-all">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto h-[calc(100vh-140px)] space-y-5">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-400 text-red-700 px-4 py-3 rounded text-sm">{error}</div>
          )}

          {/* Timezone banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <span className="text-2xl flex-shrink-0">🌍</span>
            <div>
              <p className="text-sm font-bold text-blue-900">
                Hiring Manager Timezone: <span className="text-blue-600">{tzLabel}</span>
              </p>
              <p className="text-xs text-blue-600 mt-0.5">
                HM's current time: <span className="font-semibold">{currentHmTime}</span>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Enter the new date &amp; time in <strong>{tzLabel}</strong> time below.
              </p>
            </div>
          </div>

          {/* Current schedule */}
          {currentSchedule && (
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <h3 className="text-sm font-bold text-gray-700 mb-2">Current Schedule ({tzLabel})</h3>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex items-center gap-2"><span>📅</span><span>{currentSchedule.date}</span></div>
                <div className="flex items-center gap-2">
                  <span>🕐</span>
                  <span>{currentSchedule.time} <span className="text-gray-400 text-xs">({tzLabel})</span></span>
                </div>
              </div>
            </div>
          )}

          {/* New Date */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              New Date * <span className="font-normal text-gray-500 text-xs">(in {tzLabel} time)</span>
            </label>
            <input
              type="date"
              value={scheduledDate}
              onChange={e => setScheduledDate(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          {/* New Time */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              New Time * <span className="font-normal text-gray-500 text-xs">(in {tzLabel} time)</span>
            </label>
            <input
              type="time"
              value={scheduledTime}
              onChange={e => setScheduledTime(e.target.value)}
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded text-sm text-amber-800">
            <span className="font-bold">Note:</span> The hiring manager will be notified of the schedule change.
          </div>
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl hover:bg-gray-300 font-semibold transition-all">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </>
  );
}

export default EditMeetingPanel;